import {MutableRefObject, useEffect, useRef, useState} from "react";
import {BackgroundFilter, BackgroundFilterMode} from "../../helper/BackgroundFilter";
import type {WebRtcClient} from "../../webrtc/WebRtcClient";
import type {TrackType} from "@inpriton/types/webrtcTypes";
import {enqueue} from "./captureChain";
import {BACKGROUND_STORAGE_KEY, readStoredBackground} from "./deviceStorage";

const useBackgroundFilter = ({
  stream, videoStream, cameraChain, webrtc, reportPublishFailure, clearPublishFailure,
}: {
  stream: MediaStream
  videoStream: MediaStream
  // The provider's, shared with camera capture: a chain created here would not serialize with it.
  cameraChain: MutableRefObject<Promise<void>>
  webrtc: WebRtcClient
  reportPublishFailure: (kinds: TrackType[], e: unknown) => void
  clearPublishFailure: (kinds: TrackType[]) => void
}) => {
  // RISKS.md/P2.28: remembered per browser (like `cameraId`) — a personal preference, not
  // per-conference state. Lazy initialiser, so it never flashes 'none' and then corrects itself.
  const [selectedBackground, setSelectedBackground] = useState<BackgroundFilterMode>(readStoredBackground)

  // Written on every change. What re-applies a restored mode is `enableCamera` awaiting applyFilter
  // when the track lands — NOT this effect. (RISKS.md/P2.29: assuming a React dependency would fire
  // is exactly why a persisted background was never applied on a cold load.)
  useEffect(() => {
    localStorage.setItem(BACKGROUND_STORAGE_KEY, selectedBackground)
  }, [selectedBackground])

  // Read by applyFilter, which runs from promise chains that would otherwise capture a stale mode.
  const selectedBackgroundRef = useRef(selectedBackground)
  selectedBackgroundRef.current = selectedBackground
  const backgroundFilter = useRef<BackgroundFilter>(new BackgroundFilter())

  // RISKS.md/P2.29: the only code that adds tracks to `videoStream`; the join publishes whatever it holds.
  // Reads the mode through a ref, so the copy held by enableCamera/disableCamera is never stale.
  // Callers MUST serialize on `cameraChain` — that is what makes overlapping segmenter runs
  // structurally impossible.
  const applyFilter = async () => {
    const rawTracks = stream.getVideoTracks().filter(t => t.readyState === 'live')

    // Clears first and rebuilds; elsewhere only a failed capture removes tracks, and only ended ones.
    videoStream.getVideoTracks().forEach(t => videoStream.removeTrack(t))

    // No camera (off, blocked, still starting): tear the filter down rather than leaving the frame
    // loop segmenting a frozen frame and a canvas track streaming the user's last picture.
    if (!rawTracks.length) {
      backgroundFilter.current.stop()
      return
    }

    const mode = selectedBackgroundRef.current
    const filtered = await backgroundFilter.current.apply(new MediaStream(rawTracks), mode)

    // RISKS.md/P1.24: a newer apply() or a stop() owns the filter now, so this run's output is not
    // what is on screen.
    if (!filtered) return

    // Re-read after the await: apply() can take seconds on a cold start (two downloads), and
    // the camera may have been switched off or swapped in the meantime.
    if (stream.getVideoTracks().every(t => t !== rawTracks[0])) return

    const outTrack = (mode !== 'none' && filtered.getVideoTracks()[0]) || rawTracks[0]
    videoStream.addTrack(outTrack)

    if (webrtc.isInitialized()) {
      // RISKS.md/P2.15: report, don't reject — the filter ran and the preview is live, and a
      // rejection here would strand the chain's later links too.
      try {
        if (await webrtc.enableTrack(outTrack, 'camera')) clearPublishFailure(['video'])
      } catch (e) {
        reportPublishFailure(['video'], e)
      }
    }
  }

  // Refs so enableCamera/disableCamera always call the CURRENT closure (they are created once per
  // render but invoked from promise chains that outlive it).
  const applyFilterRef = useRef(applyFilter)
  applyFilterRef.current = applyFilter

  // Same queue as camera changes, so a mode switch can never interleave with a capture swap. Deps
  // are [selectedBackground] ALONE: the provider's [selectedCamera] effect already applies the
  // filter through enableCamera(), so adding it here would double-apply and publish twice.
  const backgroundEffectRan = useRef(false)
  useEffect(() => {
    // RISKS.md/P2.38: skip the FIRST run — the mount capture already applies the current mode when
    // it installs the track. Every later run MUST fire: this is the only thing that reacts to a
    // mode change.
    if (!backgroundEffectRan.current) {
      backgroundEffectRan.current = true
      return
    }
    enqueue(cameraChain, 'background change', () => applyFilterRef.current())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBackground])

  return { selectedBackground, setSelectedBackground, applyFilterRef }
}

export default useBackgroundFilter
